import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle, Clock, User, Send, MessageSquare, AlertTriangle } from "lucide-react";
import moment from "moment";
import { INCIDENT_SEVERITY, INCIDENT_TYPE_LABEL } from "../../constants/incidents";
import { updateIncidentSac } from "../../api/packages";
import { showToast } from "../../utils/showToast";
import { apiErrorMessage } from "../../utils/apiErrorMessage";
import Spinner from "../spinner/spinner";

export default function SacTab({ incident, onUpdated, readOnly }) {
  const { t } = useTranslation();
  const [comment, setComment] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);
  const [submittingResolve, setSubmittingResolve] = useState(false);

  const sac = incident.sac || {};
  const isResolved = sac.resolved === true || !!incident.resolvedAt;
  const isRequired = sac.required !== false;

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;

    setSubmittingComment(true);
    try {
      const response = await updateIncidentSac({
        incidentId: incident._id,
        comment: comment.trim(),
      });
      if (response.success) {
        showToast(t("Comentario agregado correctamente"), true);
        setComment("");
        onUpdated && onUpdated(response.data);
      } else {
        showToast(response.error?.message || t("Error al agregar el comentario"), false);
      }
    } catch (err) {
      showToast(apiErrorMessage(err, t("Error al agregar el comentario")), false);
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleMarkResolved = async () => {
    if (!window.confirm(t("¿Marcar SAC como resuelto?"))) return;
    setSubmittingResolve(true);
    try {
      const response = await updateIncidentSac({
        incidentId: incident._id,
        resolved: true,
      });
      if (response.success) {
        showToast(t("SAC marcado como resuelto"), true);
        onUpdated && onUpdated(response.data);
      } else {
        showToast(response.error?.message || t("Error al resolver"), false);
      }
    } catch (err) {
      showToast(apiErrorMessage(err, t("Error al resolver")), false);
    } finally {
      setSubmittingResolve(false);
    }
  };

  return (
    <div>
      {/* Banner de estado */}
      {isResolved ? (
        <div className="tab-section__resolved-banner">
          <CheckCircle size={16} />
          {t("SAC resuelto")}
          {sac.resolvedAt && (
            <span style={{ marginLeft: "auto", fontSize: "0.78rem", fontWeight: 400 }}>
              {moment(sac.resolvedAt).format("DD/MM/YYYY HH:mm")}
            </span>
          )}
        </div>
      ) : isRequired && (
        <div className="tab-section__pending-banner">
          <AlertTriangle size={16} />
          {t("Pendiente de resolución SAC")}
        </div>
      )}

      {/* Información principal */}
      <div className="tab-section">
        <div className="tab-section__title">
          <AlertTriangle size={14} />
          {t("Información de la incidencia")}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-[0.6rem]">
          <div className="incident-modal__info-card">
            <div className="incident-modal__info-content">
              <span className="incident-modal__info-label">{t("Criticidad")}</span>
              <span className="incident-modal__info-value">
                <span className={`incident-badge incident-badge--${incident.severity}`}>
                  {t(INCIDENT_SEVERITY[incident.severity] || incident.severity)}
                </span>
              </span>
            </div>
          </div>
          <div className="incident-modal__info-card">
            <div className="incident-modal__info-content">
              <span className="incident-modal__info-label">{t("Tipo")}</span>
              <span className="incident-modal__info-value" style={{ fontSize: "0.8rem" }}>
                {t(INCIDENT_TYPE_LABEL[incident.incidentType] || incident.incidentType) || "—"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Descripción */}
      <div className="tab-section">
        <div className="tab-section__title">
          <MessageSquare size={14} />
          {t("Descripción")}
        </div>
        <div className="tab-section__description-box">
          {incident.description || "—"}
        </div>
      </div>

      {/* Comentarios */}
      {incident.comments && incident.comments.length > 0 && (
        <div className="tab-section">
          <div className="tab-section__title">
            <MessageSquare size={14} />
            {t("Comentarios")} ({incident.comments.length})
          </div>
          {incident.comments.map((c, i) => (
            <div key={i} className="incident-comment">
              <div className="incident-comment__header">
                <div className="incident-comment__user">
                  <User size={13} />
                  <span>
                    {c.createdBy?.name
                      ? `${c.createdBy.name}${c.createdBy.lastName ? ` ${c.createdBy.lastName}` : ""}`
                      : t("Usuario desconocido")}
                  </span>
                </div>
                <div className="incident-comment__date">
                  <Clock size={13} />
                  <span>{moment(c.createdAt).format("DD/MM/YYYY HH:mm")}</span>
                </div>
              </div>
              <p className="incident-comment__text">{c.comment}</p>
            </div>
          ))}
        </div>
      )}

      {/* Acciones (solo si no resuelto y no readOnly) */}
      {!isResolved && !readOnly && (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
          <form onSubmit={handleAddComment} style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            <textarea
              className="comment-textarea"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t("Agregar comentario SAC...")}
              rows={2}
            />
            <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
              <button
                type="submit"
                className="btn-resolve btn-resolve--outline btn-resolve--sm"
                disabled={submittingComment || !comment.trim()}
              >
                {submittingComment ? <Spinner /> : <Send size={14} />}
                {t("Agregar comentario")}
              </button>
            </div>
          </form>

          <button
            type="button"
            className="btn-resolve btn-resolve--primary"
            onClick={handleMarkResolved}
            disabled={submittingResolve}
            style={{ width: "100%", justifyContent: "center" }}
          >
            {submittingResolve ? <Spinner /> : <CheckCircle size={16} />}
            {t("Marcar SAC como resuelto")}
          </button>
        </div>
      )}
    </div>
  );
}
